import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { api } from '../hooks/useApi';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import './PorcRecommandation.css';

const FORMATS_OPTIONS = [
  { value: 'PE400',  label: 'PE400 — Format 400g (16$)' },
  { value: 'PE1000', label: 'PE1000 — Format 1kg (30$)'  },
];

const CONFIANCE_CONFIG = {
  faible  : { classe: 'confiance-faible',  iconeName: 'attention', label: 'Faible' },
  moyenne : { classe: 'confiance-moyenne', iconeName: 'info',      label: 'Moyenne' },
  élevée  : { classe: 'confiance-elevee',  iconeName: 'succes',    label: 'Élevée' },
};

export default function PorcRecommandation() {
  const [kgFini, setKgFini]       = useState('20');
  const [format, setFormat]       = useState('PE400');
  const [resultat, setResultat]   = useState(null);
  const [yieldStats, setYieldStats] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [erreur, setErreur]       = useState('');

  useEffect(() => {
    api.get('/porc/yield/stats').then(setYieldStats).catch(() => {});
  }, []);

  const calculer = async () => {
    const kg = parseFloat(kgFini);
    if (!kg || kg <= 0) {
      setErreur('Saisissez une quantité valide (kg > 0)');
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const res = await api.get(`/porc/recommendation?kg_fini=${kg}&format=${format}`);
      setResultat(res);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur de calcul');
    } finally {
      setLoading(false);
    }
  };

  const confiance = resultat ? CONFIANCE_CONFIG[resultat.confiance] || CONFIANCE_CONFIG.faible : null;

  // Données graphique: yield par batch
  const donneesYield = yieldStats?.batches_detail?.filter(b => b.yield_global > 0).map(b => ({
    nom          : b.nom.length > 12 ? b.nom.slice(0, 12) + '…' : b.nom,
    yield_global : b.yield_global,
    yield_cuisson: b.yield_cuisson || null,
  })) || [];

  const yieldMoyen = yieldStats?.yield_moyen_global;

  return (
    <div className="porc-recommandation">
      <div className="page-header">
        <div className="header-nav">
          <Link to="/production/porc" className="btn btn-ghost btn--sm">
            <Icon name="retour" size={13} /> Retour production
          </Link>
        </div>
        <h1 className="page-title">Calculateur d'achat — Porc Effiloché</h1>
        <p className="muted" style={{ marginTop: 'var(--space-2)', fontSize: 13 }}>
          Basé sur l'historique de yield de vos batches pour estimer la viande crue nécessaire.
        </p>
      </div>

      <div className="reco-layout">

        {/* ── Colonne gauche : saisie + résultat ── */}
        <div className="reco-main">

          {/* Formulaire */}
          <div className="card">
            <div className="card-header">
              <div className="card-header-left">
                <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                  <Icon name="auto" size={16} />
                </span>
                <span className="card-title">Paramètres de production</span>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Quantité désirée (kg fini)</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={kgFini}
                  onChange={e => setKgFini(e.target.value)}
                  placeholder="ex: 20"
                />
              </div>
              <div className="form-field">
                <label>Format cible</label>
                <select
                  value={format}
                  onChange={e => setFormat(e.target.value)}
                >
                  {FORMATS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {erreur && (
              <div className="error-inline" style={{ marginBottom: 'var(--space-3)' }}>
                <Icon name="erreur" size={15} />{erreur}
              </div>
            )}
            <button className="btn btn-primary" onClick={calculer} disabled={loading}>
              {loading
                ? 'Calcul en cours...'
                : <><Icon name="auto" size={14} /> Calculer la recommandation</>}
            </button>
          </div>

          {/* Résultat */}
          {resultat && (
            <div className="card reco-resultat">
              <div className="reco-titre">
                <div className="card-header-left">
                  <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                    <Icon name="analytics" size={16} />
                  </span>
                  <span className="card-title">Recommandation d'achat</span>
                </div>
                {confiance && (
                  <span className={`confiance-badge ${confiance.classe}`}>
                    <Icon name={confiance.iconeName} size={12} /> Confiance {confiance.label}
                  </span>
                )}
              </div>

              {resultat.nb_batches_historique < 3 && (
                <div className="reco-avertissement">
                  <Icon name="attention" size={16} />
                  <p style={{ margin: 0 }}>
                    Complétez au moins 3 batches avec yield tracking pour des recommandations fiables.
                    ({resultat.nb_batches_historique} batch{resultat.nb_batches_historique > 1 ? 'es' : ''} analysé{resultat.nb_batches_historique > 1 ? 's' : ''} pour l'instant)
                  </p>
                </div>
              )}

              <div className="reco-grille">
                <div className="reco-metrique principal">
                  <span className="reco-label">Viande crue à acheter</span>
                  <span className="reco-valeur gold">{resultat.kg_cru_necessaire} kg</span>
                </div>
                <div className="reco-metrique">
                  <span className="reco-label">Yield utilisé</span>
                  <span className="reco-valeur">{resultat.yield_utilise}%</span>
                </div>
                <div className="reco-metrique">
                  <span className="reco-label">Sacs estimés ({format})</span>
                  <span className="reco-valeur">{resultat.sacs_produits_estimes} sacs</span>
                </div>
                <div className="reco-metrique">
                  <span className="reco-label">Coût estimé</span>
                  <span className="reco-valeur danger">{resultat.cout_estime.toFixed(2)} $</span>
                </div>
                <div className="reco-metrique">
                  <span className="reco-label">Revenu potentiel</span>
                  <span className="reco-valeur success">{resultat.revenu_potentiel.toFixed(2)} $</span>
                </div>
                <div className="reco-metrique">
                  <span className="reco-label">Marge estimée</span>
                  <span className={`reco-valeur ${resultat.marge_estimee_pct >= 0 ? 'success' : 'danger'}`}>
                    {resultat.marge_estimee_pct.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="reco-formule">
                <span className="muted" style={{ fontSize: 12 }}>
                  Formule : {resultat.kg_fini_desire} kg fini ÷ {resultat.yield_utilise}% = {resultat.kg_cru_necessaire} kg cru
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── Colonne droite : historique yield ── */}
        <div className="reco-sidebar">
          <div className="card">
            <div className="card-header">
              <div className="card-header-left">
                <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                  <Icon name="courbe" size={16} />
                </span>
                <span className="card-title">Historique — Yield par batch</span>
              </div>
            </div>

            {!yieldStats || yieldStats.nb_batches_analyses === 0 ? (
              <div className="empty-state">
                <Icon name="courbe" size={32} />
                <span className="empty-state__titre">Aucune donnée</span>
                <span>Complétez l'étape cuisson lors de vos prochaines productions.</span>
              </div>
            ) : (
              <>
                {/* Stats résumées */}
                <div className="yield-stats-resume">
                  <div className="yield-stat">
                    <span className="yield-stat-label">Yield moyen global</span>
                    <span className="yield-stat-val gold">{yieldStats.yield_moyen_global?.toFixed(1)}%</span>
                  </div>
                  {yieldStats.yield_moyen_cuisson && (
                    <div className="yield-stat">
                      <span className="yield-stat-label">Yield moyen cuisson</span>
                      <span className="yield-stat-val">{yieldStats.yield_moyen_cuisson?.toFixed(1)}%</span>
                    </div>
                  )}
                  {yieldStats.meilleur_yield && (
                    <div className="yield-stat">
                      <span className="yield-stat-label">Meilleur yield</span>
                      <span className="yield-stat-val success">
                        {yieldStats.meilleur_yield.yield_global.toFixed(1)}% — {yieldStats.meilleur_yield.batch_nom}
                      </span>
                    </div>
                  )}
                  {yieldStats.pire_yield && (
                    <div className="yield-stat">
                      <span className="yield-stat-label">Plus bas yield</span>
                      <span className="yield-stat-val warning">
                        {yieldStats.pire_yield.yield_global.toFixed(1)}% — {yieldStats.pire_yield.batch_nom}
                      </span>
                    </div>
                  )}
                </div>

                {/* Graphique barres yield */}
                {donneesYield.length > 0 && (
                  <div className="yield-chart-container">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={donneesYield} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="nom" stroke="var(--muted)" tick={{ fontSize: 10 }} />
                        <YAxis stroke="var(--muted)" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                        <Tooltip
                          contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                          formatter={(val, name) => val != null ? [`${val.toFixed(1)}%`, name] : ['—', name]}
                        />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                        {yieldMoyen && (
                          <ReferenceLine
                            y={yieldMoyen}
                            stroke="var(--accent)"
                            strokeDasharray="4 3"
                            label={{ value: `Moy. ${yieldMoyen.toFixed(1)}%`, fill: 'var(--accent)', fontSize: 10, position: 'insideTopRight' }}
                          />
                        )}
                        <Bar dataKey="yield_global"  fill="var(--accent)" name="Yield global (%)"  radius={[3, 3, 0, 0]} />
                        <Bar dataKey="yield_cuisson" fill="var(--info)"   name="Yield cuisson (%)" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
